<template>
  <div>
    <transition name="fade">
      <div
        v-if="show"
        :class="{ show: backdrop }"
        class="F-modal-backdrop"
        @click.prevent="dismissible ? close() : null"
      ></div>
    </transition>
    <transition :name="animation">
      <div
        v-if="show"
        :id="`F-modal-${id}`"
        :ref="`F-modal-${id}`"
        :aria-labelledby="`modal-${id}-title`"
        class="F-modal"
        tabindex="-1"
      >
        <div v-if="!hideHeader" class="header">
          <h3 class="title">{{ title }}</h3>
          <button v-if="showClose" class="icon" @click="close">
            <img alt="" src="@/assets/icons/plus.svg" />
          </button>
        </div>
        <slot>
          <div class="body">
            <p class="body-text">
              {{ body }}
            </p>
          </div>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script>
import { generateElementId } from "@/utils";

/**
 * Modals are streamlined, but flexible dialog prompts. They support a number of use cases from user notification to completely custom content and feature a handful of helpful sub-components, sizes, variants, accessibility, and more.
 */
export default {
  name: "Modal",
  props: {
    /**
     * Triggers the modal component visibility.
     */
    show: {
      type: Boolean,
      required: true,
    },
    /**
     * Modal heading/header/title text.
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Modal body content. Overwritten by the named `slot` if custom content is required
     */
    body: {
      type: String,
    },
    /**
     * Title is a required prop used in assigning an `id` to the modal. If you don't need a title to be visible set this prop to `true`
     */
    hideHeader: {
      type: Boolean,
      default: false,
    },
    /**
     * When set, enables the dismiss close button. This will add a close X button.
     */
    dismissible: {
      type: Boolean,
      default: false,
    },
    /**
     * Renders the darkened background of the modal. Defaults to `true`
     */
    backdrop: {
      type: Boolean,
      default: true,
    },
    /**
     * Sets the animation for the modal transition on screen. Set to `false` for no animations.
     */
    animation: {
      type: String,
      default: "flip",
    },
    showClose: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      focusableElements: null,
    };
  },
  computed: {
    modal() {
      return document.getElementById(`modal-${this.name}`);
    },
    id() {
      return generateElementId("input", this.title);
    },
    firstFocusableEl() {
      return this.focusableElements?.[0];
    },
    lastFocusableEl() {
      return this.focusableElements?.[this.focusableElements.length - 1];
    },
  },
  methods: {
    close() {
      this.$emit("close", false);
    },
    getFocusableElements() {
      this.focusableElements = this.$el?.querySelectorAll?.(
        'a, button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), details, [tabindex]:not([tabindex="-1"])'
      );
    },
    handleFocus(event) {
      const TAB_CODE = 9;
      const ESC_CODE = 27;

      const handleForwardTab = () => {
        if (document.activeElement.isSameNode(this.lastFocusableEl)) {
          event.preventDefault();
          this.firstFocusableEl.focus();
        }
      };

      const handleBackwardTab = () => {
        if (document.activeElement.isSameNode(this.firstFocusableEl)) {
          event.preventDefault();
          this.lastFocusableEl.focus();
        }
      };

      switch (event.keyCode) {
        case TAB_CODE:
          if (
            !!this.focusableElements.length &&
            this.focusableElements.length === 1
          ) {
            event.preventDefault();
            break;
          }

          if (event.shiftKey) {
            handleBackwardTab();
          } else {
            handleForwardTab();
          }

          break;
        case ESC_CODE:
          this.close();
      }
    },
    focusOnFirstElement() {
      if (!this.focusableElements.length) {
        return;
      }

      this.$nextTick(() =>
        this.dismissible
          ? this.focusableElements?.[1]?.focus?.()
          : this.firstFocusableEl.focus()
      );
    },
  },
  beforeDestroy() {
    if (this.show) {
      this.$emit("modal-hidden");
    }
    document.removeEventListener("keydown", this.handleFocus);
  },
  mounted() {
    this.$nextTick(function () {
      this.getFocusableElements();
      this.focusOnFirstElement();
    });
    document.addEventListener("keydown", this.handleFocus);
  },
};
</script>

<style lang="scss" scoped>
.F-modal-backdrop {
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  z-index: 9998;

  &.show {
    background: #c4c4c4;
    backdrop-filter: blur(5px);
  }
}

.F-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  max-height: 90%;
  max-width: Min(716px, 90vw);
  box-sizing: border-box;
  transform: translate(-50%, -50%);
  /* padding: 36px; */
  z-index: 9999;
  background: #fff;
  box-shadow: 6px 0px 20px #00000029;
  border-radius: 12px;
  overflow: auto;

  /* @media (min-width: 800px) {
    width: unset;
  } */

  .header {
    display: grid;
    justify-content: space-between;
    align-items: center;
    grid-template-columns: 1fr 13px;
    grid-gap: 15px;
    padding: 36px;

    .title {
      font-size: 2rem;
      line-height: 140%;
    }

    button {
      width: 28px;
      height: 28px;
      background: map-get($colours, purple);
      border-radius: 50%;
      border: 2px solid black;
      display: grid;
      place-items: center;

      img {
        width: 50%;
        filter: brightness(0);
        transform: rotate(45deg);
      }
    }
  }

  .body {
    padding: 0 36px;

    .body-text {
      font-size: 14px;
      line-height: 140%;
    }
  }
}

.scale-enter-active {
  animation: scale 0.5s cubic-bezier(0.39, 0.575, 0.565, 1);
}

.scale-leave-active {
  animation: scale 0.5s cubic-bezier(0.39, 0.575, 0.565, 1) reverse;
}

@keyframes scale {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.swirl-enter-active {
  animation: swirl 0.6s ease-out;
}

.swirl-leave-active {
  animation: swirl 0.6s ease-out reverse;
}

@keyframes swirl {
  0% {
    transform: translate(-50%, -50%) rotate(-540deg) scale(0);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) rotate(0) scale(1);
    opacity: 1;
  }
}

.flip-enter-active {
  animation: flip 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.flip-leave-active {
  animation: flip 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) reverse;
}

@keyframes flip {
  0% {
    transform: translate(-50%, -50%) rotate3d(-1, 1, 0, -80deg);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) rotate3d(1, 1, 0, 0deg);
    opacity: 1;
  }
}

.swing-enter-active {
  animation: swing 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.swing-leave-active {
  animation: swing 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) reverse;
}

@keyframes swing {
  0% {
    transform: translate(-50%, -50%) rotateX(-100deg);
    transform-origin: top;
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) rotateX(0deg);
    transform-origin: top;
    opacity: 1;
  }
}

.slide-enter-active {
  animation: slide 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.slide-leave-active {
  animation: slide 0.6s cubic-bezier(0.23, 1, 0.32, 1) reverse;
}
@keyframes slide {
  0% {
    transform: translate(-50%, -1000px) scaleY(2.5) scaleX(0.2);
    transform-origin: 50% 0%;
    filter: blur(40px);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) scaleY(1) scaleX(1);
    transform-origin: 50% 50%;
    filter: blur(0);
    opacity: 1;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
